import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Cpu,
  ListChecks,
  Loader2,
  Rocket,
  XCircle,
} from "lucide-react";

import { api, ApiError } from "@/api/client";
import { useAlert } from "@/components/ConfirmDialog";
import { useErrorKindReference } from "@/api/local/errorKinds";
import {
  usePoolPlacements,
  usePoolProvisioningErrors,
  usePoolReplicaErrors,
} from "@/api/local/pools";
import {
  POOL_PHASE_LABELS,
  POOL_PHASES,
  poolPhaseStepState,
  useAbortRollout,
  useRollouts,
  type ActiveRollout,
  type PoolRolloutPlan,
  type RolloutReplicaProgressOut,
} from "@/api/local/rollout";
import {
  draftWorkloadOf,
  hashWorkloadSpec,
  liveWorkloadOf,
  type WorkloadSpec,
} from "@/api/local/workload";
import type {
  Pool,
  PoolProvisioningError,
  PoolReplicaError,
  Replica,
  ReplicasDesired,
  ReplicaStatus,
  ResolvedPlacement,
  Service,
} from "@/api/types";
import { DrainActions, type DrainActionKind } from "@/components/DrainActions";
import { Pill, type PillTone } from "@/components/Pill";
import { RolloutModal } from "@/components/RolloutModal";
import { Section } from "@/components/Section";
import { eventsHref } from "@/workloads/eventsHref";
import { humanCloudLabel } from "@/workloads/tree-data";

interface Props {
  pool: Pool;
  service: Service;
  replicas: Replica[];
}

export function PoolReplicas({ pool, service, replicas }: Props) {
  const queryClient = useQueryClient();
  const alert = useAlert();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [showRollout, setShowRollout] = useState(false);

  const rollouts = useRollouts();
  const resolvedPlacements = usePoolPlacements(pool.id);

  // The pool's "intended" spec is the service's saved draft (or live
  // when there are no pending changes). We don't model per-pool
  // workload overrides yet — that's a follow-up, and would come in via
  // Pool.extra or a dedicated overrides column.
  const live: WorkloadSpec = liveWorkloadOf(service);
  const liveEmpty = !live.image;
  const draft: WorkloadSpec = draftWorkloadOf(service);
  const targetHash = hashWorkloadSpec(draft);
  const liveHashOnPool = rollouts.poolLiveHash[pool.id] ?? null;
  const isOutOfDate = liveHashOnPool !== null && liveHashOnPool !== targetHash;

  // The active rollout *touching this pool* — covers both pool-scoped
  // (the operator hit "Roll out" on this pool) and service-scoped
  // (a service-wide rollout cascading through this pool's row in the
  // plan). Either case wants the progress UI inline on this page.
  const planForActivePool = useMemo<PoolRolloutPlan | null>(() => {
    if (!rollouts.active) return null;
    return (
      rollouts.active.plan.find((p) => p.pool_id === pool.id) ?? null
    );
  }, [rollouts.active, pool.id]);
  const isThisPoolRolling = planForActivePool !== null;

  // Narrow `rollouts.active` once for the section-header surfaces
  // (phase pill, ready count, Abort button) — those used to live
  // inside `PoolRolloutPanel` but were promoted to the header so the
  // operator sees rollout state without scrolling past the panel.
  const activeForPool = isThisPoolRolling ? rollouts.active : null;

  const abortRollout = useAbortRollout();
  const isAbortable =
    !!activeForPool &&
    (activeForPool.state === "running" || activeForPool.state === "pending");

  // "Another rollout is in flight" disables the Roll-out button. Only
  // counts as *other* if it doesn't touch this pool — a service-scoped
  // rollout that includes this pool already shows progress here, so
  // the button should let the operator open the modal to see the
  // service-wide picture.
  const otherActive =
    rollouts.active && !isThisPoolRolling ? rollouts.active : null;

  // Per-replica rollout context lookup. Map<replica_id, progress> so
  // the row renderer can colour each row with its blue/green role and
  // surface skypilot's launch-log tail without a second pass over the
  // active-rollout payload.
  const replicaProgressById = useMemo(() => {
    const map = new Map<number, RolloutReplicaProgressOut>();
    if (planForActivePool) {
      for (const r of planForActivePool.replicas_progress ?? []) {
        map.set(r.replica_id, r);
      }
    }
    return map;
  }, [planForActivePool]);

  // How many green replicas does the rollout still expect that don't
  // yet have a DB row? The reconciler only inserts a `Replica` row
  // once skypilot reports the new replica back via `sky.serve.status`,
  // which can lag the rollout engine's `provisioning` transition by
  // up to one reconcile interval. Render placeholder rows so the
  // operator sees "two green replicas are coming" instead of an empty
  // table during that window. Once we're past `health-grace` the
  // engine has already counted enough greens, so we stop showing
  // ghosts.
  const greenPlaceholders = useMemo(() => {
    if (!planForActivePool) return 0;
    if (
      planForActivePool.phase !== "preflight" &&
      planForActivePool.phase !== "provisioning" &&
      planForActivePool.phase !== "health-grace"
    ) {
      return 0;
    }
    const greenInDb = (planForActivePool.replicas_progress ?? []).filter(
      (r) => r.role === "green",
    ).length;
    return Math.max(0, planForActivePool.target_ready - greenInDb);
  }, [planForActivePool]);

  // The deploy target for this pool is what the *reconciler* will
  // converge on after the rollout, not the number of replicas alive
  // right now. On first deploy `replicas.length` is 0, but the
  // controller still clamps the per-pool desired count up to
  // `pool.min_replicas`, so we mirror that floor here. When a manual
  // pin / autoscaler row exists in `replicas_desired`, we honour it
  // (also clamped to the pool's [min, max] window).
  const desiredQuery = useQuery({
    queryKey: ["service", service.id, "desired"],
    queryFn: () =>
      api.get<ReplicasDesired[]>(`/services/${service.id}/desired`),
  });
  const targetReplicas = useMemo(
    () =>
      computeTargetReplicas(
        pool,
        (desiredQuery.data ?? []).find((r) => r.pool_id === pool.id) ?? null,
      ),
    [pool, desiredQuery.data],
  );

  const planForThisPool = useMemo(
    () => [
      {
        pool_id: pool.id,
        pool_name: pool.sky_service_name,
        replicas: targetReplicas,
        target: draft,
      },
    ],
    [pool.id, pool.sky_service_name, targetReplicas, draft],
  );

  const [pendingFor, setPendingFor] = useState<{
    id: number;
    kind: DrainActionKind;
  } | null>(null);
  const action = useMutation({
    mutationFn: ({ id, kind }: { id: number; kind: DrainActionKind }) =>
      api.post<unknown>(`/replicas/${id}/action?action=${kind}`),
    onMutate: ({ id, kind }) => {
      setPendingFor({ id, kind });
    },
    onSettled: () => {
      setPendingFor(null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["replicas", "all"] });
      queryClient.invalidateQueries({ queryKey: ["replicas"] });
    },
    onError: (err, { kind }) => {
      // Without this the spinner just clears (onSettled) and the failed
      // drain/down/restart vanishes silently. Surface the controller's
      // reason via the app-wide alert dialog (ApiError.message is
      // already the extracted FastAPI `detail`).
      void alert({
        title: `Failed to ${kind} replica`,
        tone: "danger",
        description:
          err instanceof ApiError || err instanceof Error
            ? err.message
            : `Failed to ${kind} replica.`,
      });
    },
  });

  // Per-replica live counters (active WebSockets, in-flight short-lived).
  // The controller exposes them at /replicas/{id}/runtime; values are
  // sampled from Prometheus + Envoy stats, so we poll on a sane cadence
  // rather than streaming.
  const runtime = useQuery<Record<number, ReplicaRuntime>>({
    queryKey: ["pool", pool.id, "replica-runtime"],
    queryFn: () =>
      api.get<Record<number, ReplicaRuntime>>(
        `/pools/${pool.id}/replica-runtime`,
      ),
    staleTime: 4_000,
    refetchInterval: 5_000,
  });

  return (
    <>
      {/* Page-level alert: provisioning failures are surfaced *above*
          the Section header so the operator sees them before any
          chrome / pills / table content. The previous inline placement
          (below the section header, above the table) was easy to miss
          on tall viewports — moving the banner to the top of the tab
          content matches how operators actually scan the page. */}
      <ProvisioningErrorsCallout pool={pool} />
      <ReplicaErrorsCallout pool={pool} replicas={replicas} />
      <Section
      title={`Replicas in ${pool.sky_service_name}`}
      icon={<Cpu />}
      count={
        <span>
          {replicas.length} live · cap {pool.max_replicas}
        </span>
      }
      actions={
        <button
          className="btn btn--primary"
          disabled={!!otherActive}
          onClick={() => setShowRollout(true)}
          title={
            otherActive
              ? "another rollout is in progress"
              : "Roll out this pool only (blue/green)"
          }
        >
          <Rocket />
          {isThisPoolRolling
            ? "View rollout"
            : isOutOfDate
              ? "Roll out…"
              : "Roll out"}
        </button>
      }
      flush
    >
      {isThisPoolRolling && rollouts.active && planForActivePool && (
        <PoolRolloutPanel
          active={rollouts.active}
          plan={planForActivePool}
          isOutOfDate={isOutOfDate}
          isAbortable={isAbortable}
          onAbort={
            activeForPool
              ? () => void abortRollout(activeForPool.id)
              : undefined
          }
        />
      )}
      {!planForActivePool && isOutOfDate && (
        <div
          className="muted"
          style={{
            margin: "0 14px 10px",
            display: "inline-flex",
            gap: 8,
            alignItems: "center",
            fontSize: 12,
          }}
        >
          <Pill tone="warn">spec drift</Pill>
          <span>
            this pool is running an older spec — roll out to converge
          </span>
        </div>
      )}
      <NoProvidersBanner pool={pool} />
      <div className="table-wrap">
        <table className="data data--replicas">
          <thead>
            <tr>
              <th style={{ width: 24 }}></th>
              <th style={{ textAlign: "right", width: 48 }}>id</th>
              <th style={{ minWidth: 160 }}>Replica</th>
              <th>Endpoint</th>
              <th>
                <WhereHeader pool={pool} />
              </th>
              <th
                style={{ textAlign: "right" }}
                title="active WebSockets · short-lived in-flight"
              >
                Traffic
              </th>
              <th>Started</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {replicas.map((r) => {
              const isOpen = expanded.has(r.id);
              const rt = runtime.data?.[r.id];
              const pending =
                pendingFor && pendingFor.id === r.id ? pendingFor.kind : null;
              const cloud = inferReplicaCloud(r, resolvedPlacements);
              const progress = replicaProgressById.get(r.id) ?? null;
              const noteText =
                progress?.status_message ?? r.status_message ?? null;
              // ``status_message`` arrives from the controller in the
              // shape ``[<kind>] <line>`` for classified failures
              // (e.g. ``[user_app.failed]
              // ModuleNotFoundError: No module named 'psycopg'``).
              // Pull the kind out so we can render it as a pill next
              // to the raw line — operators scan the kind first and
              // read the line second.
              const noteParts = parseClassifiedNote(noteText);
              const showRawStatus =
                progress?.sky_status_raw ?? r.sky_status_raw ?? null;
              const statusPill = replicaPillSpec(
                r.status,
                progress?.role ?? null,
                planForActivePool?.phase ?? null,
              );
              const pillTitle = [
                showRawStatus && showRawStatus !== r.status
                  ? `sky: ${showRawStatus}`
                  : null,
                noteText,
              ]
                .filter(Boolean)
                .join(" — ") || undefined;
              const roleLabel = progress
                ? progress.role === "green"
                  ? "green"
                  : "blue"
                : null;
              return (
                <Fragment key={r.id}>
                  <tr
                    className={`expandable${
                      progress
                        ? ` rollout-row rollout-row--${progress.role}`
                        : ""
                    }`}
                    onClick={() => {
                      const next = new Set(expanded);
                      if (next.has(r.id)) next.delete(r.id);
                      else next.add(r.id);
                      setExpanded(next);
                    }}
                  >
                    <td className="muted">
                      {isOpen ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                    </td>
                    <td className="num">{r.id}</td>
                    <td>
                      <Pill
                        tone={statusPill.tone}
                        pulse={statusPill.pulse}
                        title={pillTitle}
                      >
                        {roleLabel ? `${roleLabel} · ` : ""}
                        {statusPill.label}
                      </Pill>
                    </td>
                    <td className="mono">
                      {r.external_ip ?? <span className="muted">—</span>}
                      {r.external_port ? `:${r.external_port}` : ""}
                    </td>
                    <td>
                      <WhereCell
                        cloud={cloud}
                        region={r.region}
                        instance={r.instance_type}
                      />
                    </td>
                    <td
                      className="num mono"
                      title="active WebSockets · short-lived in-flight"
                    >
                      {rt ? (
                        <>
                          <span>{rt.active_websockets}</span>
                          <span className="muted"> · </span>
                          <span>{rt.in_flight_short_lived}</span>
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="mono muted">
                      {r.started_at ? (
                        <span title={new Date(r.started_at).toLocaleString()}>
                          {formatRelativeShort(
                            Date.now() - new Date(r.started_at).getTime(),
                          )}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td
                      className="actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          gap: 4,
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Link
                          to={eventsHref({
                            // Stay inside the pool's SubNav: this jumps
                            // to the Pool > Events tab with `replica_id`
                            // layered on top as a removable chip, so
                            // the operator can drop the replica filter
                            // without leaving the pool.
                            focus: { type: "pool", id: pool.id },
                            replica_id: r.id,
                          })}
                          className="btn btn--ghost btn--sm"
                          title="View events for this replica"
                          aria-label="events"
                        >
                          <ListChecks size={12} />
                        </Link>
                        <DrainActions
                          status={r.status}
                          pendingKind={pending}
                          onAction={(kind) =>
                            action.mutate({ id: r.id, kind })
                          }
                          compact
                        />
                      </span>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={`${r.id}-detail`} className="detail-row">
                      <td colSpan={8}>
                        <div className="kv">
                          <span className="k">cluster</span>
                          <span className="v mono">
                            {r.sky_cluster_name ?? "—"}
                          </span>
                          <span className="k">last health</span>
                          <span className="v mono">
                            {r.last_health_at
                              ? new Date(r.last_health_at).toLocaleString()
                              : "—"}
                          </span>
                          {(showRawStatus &&
                            showRawStatus !== r.status) ||
                          noteText ? (
                            <>
                              <span className="k">sky status</span>
                              <span className="v mono">
                                {showRawStatus &&
                                  showRawStatus !== r.status && (
                                    <span>{showRawStatus}</span>
                                  )}
                                {noteText && (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 6,
                                      alignItems: "center",
                                    }}
                                  >
                                    {noteParts.kind && (
                                      <Pill
                                        tone={
                                          noteParts.kind.startsWith("user_app.")
                                            ? "danger"
                                            : "warn"
                                        }
                                        title="classified failure kind — see Replicas tab callout for the operator-fix hint"
                                      >
                                        {replicaErrorKindLabel(noteParts.kind)}
                                      </Pill>
                                    )}
                                    <span className="muted">
                                      {noteParts.body}
                                    </span>
                                  </div>
                                )}
                              </span>
                            </>
                          ) : null}
                          <span className="k">pods</span>
                          <span className="v">
                            {r.pods.length === 0 && (
                              <em className="muted">no pods recorded yet</em>
                            )}
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                              }}
                            >
                              {r.pods.map((p) => (
                                <span
                                  key={p.id}
                                  className="mono"
                                  style={{ fontSize: 12 }}
                                >
                                  <Pill tone="brand">
                                    {p.node_role}#{p.node_index}
                                  </Pill>{" "}
                                  {p.internal_ip ?? "—"} →{" "}
                                  {p.external_ip ?? "—"}
                                </span>
                              ))}
                            </div>
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {/* Ghost rows for green replicas the rollout expects but
                that the reconciler hasn't observed in skypilot yet. We
                stop showing these once the engine has moved past
                health-grace; by that point the engine has counted
                enough greens and any remaining gap is an inconsistency
                the operator should see. */}
            {greenPlaceholders > 0 &&
              Array.from({ length: greenPlaceholders }).map((_, i) => (
                <tr
                  key={`ghost-${i}`}
                  className="replica-ghost-row rollout-row rollout-row--green"
                  title="rollout expects a green replica here; waiting for skypilot to report it"
                >
                  <td></td>
                  <td className="num muted">—</td>
                  <td>
                    <Pill tone="warn" pulse>
                      green · provisioning
                    </Pill>
                  </td>
                  <td colSpan={5} className="muted">
                    waiting for skypilot to report this replica
                  </td>
                </tr>
              ))}
            {!replicas.length && greenPlaceholders === 0 && (
              <tr>
                <td colSpan={8} className="empty">
                  no replicas — pool is idle or pending provisioning
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <RolloutModal
        open={showRollout}
        onClose={() => setShowRollout(false)}
        scope="pool"
        scopeLabel={pool.sky_service_name}
        serviceId={service.id}
        poolId={pool.id}
        // For per-pool rollouts, "source" is what's currently running on
        // this pool's replicas. We approximate it by using the service's
        // live spec when the pool's hash matches it, or null (= treat
        // as first rollout) otherwise.
        source={
          !liveEmpty &&
          liveHashOnPool &&
          liveHashOnPool === hashWorkloadSpec(live)
            ? live
            : null
        }
        target={draft}
        plan={planForThisPool}
      />
      </Section>
    </>
  );
}

/**
 * In-page rollout progress panel — replaces the auto-opening modal.
 *
 * Renders the 8-step phase strip plus the strategy parameters
 * (surge / drain / health-grace) so the operator can see *how* this
 * rollout is configured without opening the full modal. Phase pill,
 * ready count, and Abort button live on the Section header now —
 * keeping rollout state visible without scrolling past this panel.
 */
function PoolRolloutPanel({
  active,
  plan,
  isOutOfDate,
  isAbortable,
  onAbort,
}: {
  active: ActiveRollout;
  plan: PoolRolloutPlan;
  isOutOfDate: boolean;
  isAbortable: boolean;
  onAbort?: () => void;
}) {
  // Per-green-replica state for the provisioning step's multi-dot
  // indicator. The pool's `phase` is a single value, so it can't show
  // "1/2 greens are ready, 1 is still launching" on its own — we fan
  // the provisioning dot out into one mini-dot per *target* slot and
  // colour each by the actual replica's status. Ghost slots cover the
  // window where the rollout expects a replica that skypilot hasn't
  // reported back yet (the same window the table's ghost rows cover).
  //
  // For pools with many replicas (>MAX_FANOUT_SLOTS) the per-slot
  // dots stop being readable and start blowing the column's width
  // budget, so we collapse the indicator to an aggregated summary
  // (single dot + `ready/target` count + optional `N failed`).
  const greenSlots = useMemo(
    () =>
      buildGreenSlots(
        plan.replicas_progress ?? [],
        plan.target_ready,
      ),
    [plan.replicas_progress, plan.target_ready],
  );
  const greenSummary = useMemo(
    () => summariseGreenSlots(greenSlots),
    [greenSlots],
  );
  const phasePulses =
    plan.phase !== "complete" &&
    plan.phase !== "failed" &&
    plan.phase !== "queued";
  return (
    <div className="rollout-pool" style={{ marginBottom: 12 }}>
      <div className="rollout-pool__head">
        <Pill tone={phasePillTone(plan.phase)} pulse={phasePulses}>
          {phasePulses && <Loader2 size={11} />}
          {POOL_PHASE_LABELS[plan.phase]}
        </Pill>
        {plan.target_ready > 0 && (
          <span className="rollout-pool__count mono">
            {plan.ready_green}/{plan.target_ready} green ready
          </span>
        )}
        {isOutOfDate && <Pill tone="warn">spec drift</Pill>}
        <span style={{ flex: 1 }} />
        {isAbortable && onAbort && (
          <button
            type="button"
            className="btn btn--ghost btn--sm rollout-pool__abort"
            onClick={onAbort}
            title="Abort the rollout (leave blue alive)"
          >
            <XCircle size={12} />
            Abort rollout
          </button>
        )}
      </div>
      <div className="rollout-pool__steps">
        {POOL_PHASES.map((ph) => {
          const stepState = poolPhaseStepState(plan.phase, ph);
          const isProvisioningWithFanout =
            ph === "provisioning" && greenSlots.length > 0;
          return (
            <div
              key={ph}
              className={`rollout-step rollout-step--${stepState}`}
              title={
                isProvisioningWithFanout
                  ? `${POOL_PHASE_LABELS[ph]} · ${plan.ready_green}/${plan.target_ready} green ready`
                  : POOL_PHASE_LABELS[ph]
              }
            >
              {isProvisioningWithFanout ? (
                greenSlots.length <= MAX_FANOUT_SLOTS ? (
                  <span
                    className="rollout-step__minidots"
                    aria-label={`${plan.ready_green} of ${plan.target_ready} green replicas ready`}
                  >
                    {greenSlots.map((slot, i) => (
                      <span
                        key={i}
                        className={`rollout-step__minidot rollout-step__minidot--${slot.tone}`}
                        title={slot.title}
                      />
                    ))}
                  </span>
                ) : (
                  <span
                    className="rollout-step__minidots"
                    aria-label={`${plan.ready_green} of ${plan.target_ready} green replicas ready${
                      greenSummary.failed > 0
                        ? `, ${greenSummary.failed} failed`
                        : ""
                    }`}
                    title={`${plan.ready_green}/${plan.target_ready} green ready${
                      greenSummary.failed > 0
                        ? ` · ${greenSummary.failed} failed`
                        : ""
                    }`}
                  >
                    <span
                      className={`rollout-step__minidot rollout-step__minidot--${greenSummary.tone}`}
                    />
                    <span className="rollout-step__count mono">
                      {plan.ready_green}/{plan.target_ready}
                      {greenSummary.failed > 0
                        ? ` · ${greenSummary.failed} failed`
                        : ""}
                    </span>
                  </span>
                )
              ) : (
                <span className="rollout-step__dot" />
              )}
              <span className="rollout-step__label">{ph}</span>
            </div>
          );
        })}
      </div>
      {plan.error && (
        <div className="rollout-pool__error">{plan.error}</div>
      )}
      <div className="rollout-pool__meta muted mono">
        surge {active.strategy.surge_pct}% · drain{" "}
        {active.strategy.drain_seconds}s · health-grace{" "}
        {active.strategy.health_check_grace_seconds}s · scope {active.scope}
        {active.scope === "service" ? " (cascade)" : ""}
      </div>
    </div>
  );
}

/** Tone for the rollout-phase pill shown in the Section header. */
function phasePillTone(phase: PoolRolloutPlan["phase"]): PillTone {
  if (phase === "complete") return "ok";
  if (phase === "failed") return "danger";
  if (phase === "queued") return "neutral";
  return "info";
}

interface ReplicaPillSpec {
  label: string;
  tone: PillTone;
  pulse?: boolean;
}

/**
 * Map a replica's current state to the rollout's 8-phase vocabulary
 * so the table speaks the same language as the phase strip above.
 *
 * - `pending` ↦ `queued` (sitting in skypilot's launch queue)
 * - `provisioning` ↦ `provisioning`
 * - `shutting_down` ↦ `teardown`
 * - `draining*` ↦ `draining`
 * - `ready` is steady-state; we surface it as `live` (it's serving),
 *   except for green replicas during a rollout where the *pool*'s
 *   phase tells us which leg of the journey this replica is in
 *   (`provisioning` while peers catch up, `health-grace`, `flipping`).
 *
 * `live` is a synthetic 9th label: the 8 phases describe the rollout
 * journey, but a replica that's serving traffic isn't "in a phase",
 * so we need a name for that steady state.
 */
function replicaPillSpec(
  status: ReplicaStatus,
  role: RolloutReplicaProgressOut["role"] | null,
  poolPhase: PoolRolloutPlan["phase"] | null,
): ReplicaPillSpec {
  switch (status) {
    case "failed":
      return { label: "failed", tone: "danger" };
    case "pending":
      return { label: "queued", tone: "neutral" };
    case "provisioning":
      return { label: "provisioning", tone: "warn", pulse: true };
    case "not_ready":
      return { label: "not ready", tone: "danger" };
    case "draining":
      return { label: "draining", tone: "info", pulse: true };
    case "draining_long_lived":
      return { label: "draining (WS-only)", tone: "info", pulse: true };
    case "shutting_down":
      return { label: "teardown", tone: "neutral" };
    case "ready": {
      if (role === "green" && poolPhase) {
        if (
          poolPhase === "queued" ||
          poolPhase === "preflight" ||
          poolPhase === "provisioning"
        ) {
          // Per user preference: keep this aligned with the strip's
          // `provisioning` step even though the replica itself is
          // ready — the *pool* hasn't moved past provisioning yet
          // because peers haven't caught up.
          return { label: "provisioning", tone: "warn", pulse: true };
        }
        if (poolPhase === "health-grace") {
          return { label: "health-grace", tone: "info", pulse: true };
        }
        if (poolPhase === "flipping") {
          return { label: "flipping", tone: "info", pulse: true };
        }
      }
      return { label: "live", tone: "ok" };
    }
  }
}

type GreenSlotTone = "ready" | "inflight" | "failed" | "ghost";

interface GreenSlot {
  tone: GreenSlotTone;
  title: string;
}

/**
 * Beyond this many target replicas the per-slot fan-out stops being
 * legible and starts blowing the provisioning column's width budget
 * (each slot is ~8px including gap; the column is ~100–150px wide on
 * typical viewports). Above this we fall back to an aggregated
 * `ready/target` count rendering. 8 keeps a single row under typical
 * panel widths while still letting small pools (the common case) see
 * each replica's state individually.
 */
const MAX_FANOUT_SLOTS = 8;

interface GreenSummary {
  failed: number;
  tone: GreenSlotTone;
}

/**
 * Roll a list of per-slot tones up into a single tone for the
 * compact summary rendering used when there are too many replicas to
 * fan out individually. We surface the *worst* state so a single
 * failed replica can't be hidden behind 99 healthy ones.
 */
function summariseGreenSlots(slots: ReadonlyArray<GreenSlot>): GreenSummary {
  let failed = 0;
  let inflight = 0;
  let ready = 0;
  let ghost = 0;
  for (const s of slots) {
    if (s.tone === "failed") failed += 1;
    else if (s.tone === "inflight") inflight += 1;
    else if (s.tone === "ready") ready += 1;
    else ghost += 1;
  }
  let tone: GreenSlotTone;
  if (failed > 0) tone = "failed";
  else if (inflight > 0 || ghost > 0) tone = "inflight";
  else if (ready > 0) tone = "ready";
  else tone = "ghost";
  return { failed, tone };
}

/**
 * Fan out the provisioning step's single dot into one slot per
 * *target* green replica so the operator can see which replicas have
 * settled and which are still launching. The pool's `phase` only
 * advances when *all* slots reach `ready`, so an uneven 2-pod rollout
 * (one ready, one still pulling the image) shows up here as one
 * filled dot + one pulsing dot rather than a single ambiguous
 * `provisioning` indicator.
 *
 * Ghost slots cover the brief window where the engine has counted
 * the rollout as "expecting N greens" but skypilot hasn't reported
 * one of them back yet (same condition that triggers the table's
 * ghost rows).
 */
function buildGreenSlots(
  replicasProgress: ReadonlyArray<RolloutReplicaProgressOut>,
  targetReady: number,
): GreenSlot[] {
  if (targetReady <= 0) return [];
  const greens = replicasProgress.filter((r) => r.role === "green");
  const slots: GreenSlot[] = greens.map((r) => ({
    tone: greenStatusTone(r.status),
    title: `replica#${r.sky_replica_id ?? r.replica_id} · ${
      r.sky_status_raw ?? r.status
    }`,
  }));
  const ghostCount = Math.max(0, targetReady - slots.length);
  for (let i = 0; i < ghostCount; i += 1) {
    slots.push({
      tone: "ghost",
      title: "rollout expects this replica · waiting for skypilot",
    });
  }
  return slots;
}

function greenStatusTone(s: ReplicaStatus): GreenSlotTone {
  if (s === "ready") return "ready";
  if (s === "failed" || s === "not_ready") return "failed";
  return "inflight";
}

/**
 * Mirror of the controller's `_clamp_pool` (see
 * `controller/scheduling/pool_planner.py`). Returns the replica count
 * the reconciler will actually converge on for this pool: the explicit
 * `replicas_desired` row (if any), clamped to
 * `[pool.min_replicas, pool.max_replicas]`. With no row yet — i.e.
 * first deploy of a fresh pool — `desired_count=0` clamps up to
 * `pool.min_replicas`, which is what the reconciler will bring up.
 */
export function computeTargetReplicas(
  pool: Pool,
  desired: ReplicasDesired | null,
): number {
  const lo = Math.max(0, pool.min_replicas ?? 0);
  const hi = Math.max(lo, pool.max_replicas);
  const raw = Math.max(0, desired?.desired_count ?? 0);
  return Math.max(lo, Math.min(hi, raw));
}

/**
 * Recent provisioning failures for this Pool — surfaces SkyPilot-side
 * errors (and the controller's own ``provisioning.invalid_resources``
 * preflight) inline on the Replicas tab so the operator doesn't have
 * to switch to the Events tab to figure out why this pool is sitting
 * at 0/0.
 *
 * The endpoint already time-bounds results (default last 60 minutes)
 * so the callout fades out naturally once the operator's fix takes
 * hold. On top of that we *also* hide every error that predates the
 * latest rollout touching this pool — once the operator hits "Roll
 * out…" again the previous attempt's failure becomes yesterday's
 * news and would otherwise hover next to the new (still in-flight)
 * rollout, making it look as if the *current* attempt had failed.
 * Errors emitted by the new attempt naturally have a fresher ``ts``
 * and slip through the filter unchanged. We render only the most
 * recent error big and surface the remainder as a compact "+N more"
 * link to the events tab — the goal is to make the *current* blocker
 * actionable, not to duplicate the events history here.
 */
function ProvisioningErrorsCallout({ pool }: { pool: Pool }) {
  const errors = usePoolProvisioningErrors(pool.id);
  const rollouts = useRollouts();
  // Server-driven error-kind catalogue. Lookups against ``top.kind``
  // give us the operator-facing label (``"invalid Pool resources"``)
  // and the long-form hint without duplicating the table in TS.
  const errorKinds = useErrorKindReference();
  const kindMeta = useMemo(() => {
    const map = new Map<string, { label: string; hint: string | null }>();
    for (const k of errorKinds.data?.error_kinds ?? []) {
      map.set(k.kind, { label: k.label, hint: k.hint });
    }
    return map;
  }, [errorKinds.data]);

  // Cutoff = `started_at` of the most recent rollout that touched
  // this pool (active or finished). Anything older was emitted by a
  // *prior* attempt the operator has since superseded with a fresh
  // rollout; keeping that banner around next to an in-flight rollout
  // misleads them into thinking the new attempt has already failed.
  const cutoff = useMemo<number | null>(() => {
    let latest: string | null = null;
    const consider = (r: { started_at: string; pool_progress: { pool_id: number }[] }) => {
      if (!r.pool_progress.some((p) => p.pool_id === pool.id)) return;
      if (latest === null || new Date(r.started_at) > new Date(latest)) {
        latest = r.started_at;
      }
    };
    if (rollouts.active) consider(rollouts.active);
    for (const r of rollouts.history) consider(r);
    return latest === null ? null : new Date(latest).getTime();
  }, [rollouts.active, rollouts.history, pool.id]);

  const fresh = useMemo(() => {
    if (cutoff === null) return errors;
    return errors.filter((e) => new Date(e.ts).getTime() >= cutoff);
  }, [errors, cutoff]);

  if (fresh.length === 0) return null;
  const top = fresh[0];
  const isError =
    top.severity === "critical" || top.severity === "error";
  // Tone the banner with the canonical .flash--danger / .flash--warn
  // classes so the colour matches every other operator-attention
  // banner in the app, instead of one-off inline borders. The banner
  // sits *above* the Section, not inside its body — operators were
  // missing the previous in-table callout because it scrolled with
  // the data.
  const flashTone = isError ? "flash--danger" : "flash--warn";
  const pillTone = isError ? "danger" : "warn";
  const heading = isError ? "Provisioning failed" : "Provisioning warning";
  const moreHref = eventsHref({
    focus: { type: "pool", id: pool.id },
  });
  return (
    <div
      role="alert"
      className={`flash ${flashTone} page-alert`}
      style={{ marginTop: 0, marginBottom: 14 }}
    >
      <div className="page-alert__head">
        <AlertTriangle size={18} className="page-alert__icon" />
        <span className="page-alert__title">{heading}</span>
        <Pill tone={pillTone}>
          {kindMeta.get(top.kind)?.label ?? kindLabel(top.kind)}
        </Pill>
        <span
          className="muted mono"
          style={{ fontSize: 11 }}
          title={top.ts}
        >
          {new Date(top.ts).toLocaleString()}
        </span>
        <span style={{ flex: 1 }} />
        <Link
          to={moreHref}
          className="btn btn--ghost"
          style={{ fontSize: 12 }}
        >
          {fresh.length > 1
            ? `events · ${fresh.length} recent`
            : "view events"}
        </Link>
      </div>
      {top.reason && (
        <div className="page-alert__body mono">{top.reason}</div>
      )}
      {(top.hint || kindMeta.get(top.kind)?.hint) && (
        <div className="page-alert__hint muted">
          <strong>fix:</strong>{" "}
          {top.hint || kindMeta.get(top.kind)?.hint}
        </div>
      )}
      {top.kind === "provisioning.invalid_resources" && (
        <ProvisioningErrorOffenders payload={top.payload} />
      )}
    </div>
  );
}

/**
 * Recent classified user-app failures for this Pool — surfaces
 * failures that fired *inside* the workload container (alembic / pip /
 * entrypoint scripts) inline on the Replicas tab so operators don't
 * have to drill into ``sky serve logs`` to spot a recurring fault
 * like a missing python module.
 *
 * Sister of :func:`ProvisioningErrorsCallout`; we render both above
 * the table because the failures answer different operator
 * questions (provisioning = "why didn't the pod come up?", user_app =
 * "the pod came up but the workload immediately died"). When both
 * fire we want the operator to see *both* — they imply different
 * fixes — instead of collapsing into one callout that hides the
 * second cause.
 */
function ReplicaErrorsCallout({
  pool,
  replicas,
}: {
  pool: Pool;
  replicas: Replica[];
}) {
  const errors = usePoolReplicaErrors(pool.id);
  // Server-driven catalogue covering both provisioning + user_app
  // kinds — the user_app rows give us the chip label + hint.
  const errorKinds = useErrorKindReference();
  const kindMeta = useMemo(() => {
    const map = new Map<string, { label: string; hint: string | null }>();
    for (const k of errorKinds.data?.error_kinds ?? []) {
      map.set(k.kind, { label: k.label, hint: k.hint });
    }
    return map;
  }, [errorKinds.data]);
  // Same staleness rule as ProvisioningErrorsCallout: don't show a
  // failure that predates the most recent rollout touching this
  // pool — the operator's already moved on, and dragging yesterday's
  // banner alongside an in-flight rollout is the bug we keep the
  // cutoff for.
  const rollouts = useRollouts();
  const cutoff = useMemo<number | null>(() => {
    let latest: string | null = null;
    const consider = (
      r: { started_at: string; pool_progress: { pool_id: number }[] },
    ) => {
      if (!r.pool_progress.some((p) => p.pool_id === pool.id)) return;
      if (latest === null || new Date(r.started_at) > new Date(latest)) {
        latest = r.started_at;
      }
    };
    if (rollouts.active) consider(rollouts.active);
    for (const r of rollouts.history) consider(r);
    return latest === null ? null : new Date(latest).getTime();
  }, [rollouts.active, rollouts.history, pool.id]);

  // Suppress the callout when *every* replica that hit this failure
  // has since recovered: we keep one "latest" failure per replica.id
  // and only render those whose replica is still in a non-ready
  // state. A replica that flipped FAILED → terminated → fresh launch
  // → READY shouldn't trigger a permanent banner.
  const fresh = useMemo<readonly PoolReplicaError[]>(() => {
    const recent =
      cutoff === null
        ? errors
        : errors.filter((e) => new Date(e.ts).getTime() >= cutoff);
    const live = new Map<number, PoolReplicaError>();
    for (const e of recent) {
      if (e.replica_id == null) continue;
      // ``recent`` is server-ordered DESC by ts → first hit per
      // replica.id is the most recent failure we care about.
      if (!live.has(e.replica_id)) live.set(e.replica_id, e);
    }
    // Only keep failures whose replica is still around AND not
    // healthy. ``status === 'ready'`` means the operator's already
    // recovered (e.g. a fresh image rebuild took over); keeping the
    // banner up after recovery would be misleading.
    const stillBad: PoolReplicaError[] = [];
    for (const e of live.values()) {
      const replica = replicas.find((r) => r.id === e.replica_id);
      if (!replica) {
        // Replica row is gone — likely a transient failure SkyServe
        // already cleaned up. Surface it once but don't keep it
        // pinned: the events tab carries the full history.
        continue;
      }
      if (replica.status === "ready") continue;
      stillBad.push(e);
    }
    stillBad.sort((a, b) => b.ts.localeCompare(a.ts));
    return stillBad;
  }, [errors, cutoff, replicas]);

  if (fresh.length === 0) return null;
  const top = fresh[0];
  const moreHref = eventsHref({
    focus: { type: "pool", id: pool.id },
  });
  // Always tone as danger: a workload-side failure is never a
  // benign warning — even a transient OOM means the replica is
  // dead and the pool's traffic dropped.
  const flashTone = "flash--danger";
  const pillTone = "danger";
  return (
    <div
      role="alert"
      className={`flash ${flashTone} page-alert`}
      style={{ marginTop: 0, marginBottom: 14 }}
    >
      <div className="page-alert__head">
        <AlertTriangle size={18} className="page-alert__icon" />
        <span className="page-alert__title">Workload crashed</span>
        <Pill tone={pillTone}>
          {(top.classification && kindMeta.get(top.classification)?.label) ??
            replicaErrorKindLabel(top.classification)}
        </Pill>
        {top.replica_id != null && (
          <Pill tone="brand">replica#{top.replica_id}</Pill>
        )}
        <span
          className="muted mono"
          style={{ fontSize: 11 }}
          title={top.ts}
        >
          {new Date(top.ts).toLocaleString()}
        </span>
        <span style={{ flex: 1 }} />
        <Link
          to={moreHref}
          className="btn btn--ghost"
          style={{ fontSize: 12 }}
        >
          {fresh.length > 1
            ? `events · ${fresh.length} replicas`
            : "view events"}
        </Link>
      </div>
      {top.error && (
        <div className="page-alert__body mono">{top.error}</div>
      )}
      {(top.hint ||
        (top.classification &&
          kindMeta.get(top.classification)?.hint)) && (
        <div className="page-alert__hint muted">
          <strong>fix:</strong>{" "}
          {top.hint ||
            (top.classification && kindMeta.get(top.classification)?.hint)}
        </div>
      )}
      {fresh.length > 1 && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          <span className="muted">also affecting:</span>
          {fresh.slice(1, 6).map((e) => (
            <span key={e.id} className="mono">
              <Pill tone="warn">
                {replicaErrorKindLabel(e.classification)}
              </Pill>
              {e.replica_id != null && (
                <span className="muted"> replica#{e.replica_id}</span>
              )}
            </span>
          ))}
          {fresh.length > 6 && (
            <span className="muted">+{fresh.length - 6} more</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Inline "what fields tripped the validator" rendering for the
 * controller's renderer-side preflight. We echo the offending Pool
 * fields back from the event payload so the operator can read the
 * mismatch without cross-referencing the Pool Spec tab.
 */
function ProvisioningErrorOffenders({
  payload,
}: {
  payload: PoolProvisioningError["payload"];
}) {
  const cpus = stringField(payload.cpus);
  const memory = stringField(payload.memory_gb);
  const accelerators = stringField(payload.accelerators);
  const disk = stringField(payload.disk_gb);
  const rows: Array<[string, string]> = [];
  if (cpus) rows.push(["cpus", cpus]);
  if (memory) rows.push(["memory", memory]);
  if (accelerators) rows.push(["accelerators", accelerators]);
  if (disk) rows.push(["disk_gb", disk]);
  if (rows.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        marginTop: 6,
        flexWrap: "wrap",
        fontSize: 12,
      }}
    >
      {rows.map(([k, v]) => (
        <span key={k} className="mono">
          <span className="muted">{k}=</span>
          <code>{v}</code>
        </span>
      ))}
    </div>
  );
}

function stringField(v: unknown): string | null {
  if (v === null || v === undefined || v === "") return null;
  return String(v);
}

/**
 * Parse a controller-side ``status_message`` into its classification
 * tag (e.g. ``user_app.missing_psycopg``) and the raw line. The
 * controller emits classified failures as ``[<kind>] <line>`` so the
 * WebUI can render the kind as a pill while keeping the underlying
 * line operator-readable. Lines without the bracket prefix are
 * returned as ``{ kind: null, body: line }`` so callers don't have
 * to special-case the unclassified path.
 */
function parseClassifiedNote(note: string | null): {
  kind: string | null;
  body: string;
} {
  if (!note) return { kind: null, body: "" };
  const m = /^\[([^\]]+)\]\s*(.*)$/.exec(note);
  if (!m) return { kind: null, body: note };
  return { kind: m[1] ?? null, body: m[2] ?? "" };
}

/**
 * Compact human label for a `replica.user_app_failed` classification.
 *
 * Post-v0.0.141 design: pod/docker errors are too individual to
 * enumerate per-failure-mode in the controller, so the only kind
 * the backend emits is ``user_app.failed``. The verbatim error
 * line from the log tail is rendered alongside the pill, so the
 * operator still sees the underlying root cause string. The switch
 * stays in place (rather than collapsing to a constant) so that
 * future per-kind labels — if we ever decide a particular failure
 * mode is universal enough to enumerate — are a one-liner.
 */
function replicaErrorKindLabel(kind: string): string {
  switch (kind) {
    case "user_app.failed":
      return "workload failed";
    default:
      return kind;
  }
}

/** Compact human label for a `provisioning.*` event kind. */
function kindLabel(kind: string): string {
  switch (kind) {
    case "provisioning.invalid_resources":
      return "invalid Pool resources";
    case "provisioning.no_capacity":
      return "no capacity (cloud)";
    case "provisioning.quota":
      return "quota exceeded";
    case "provisioning.disk_too_large":
      return "disk size too large (cloud cap)";
    case "provisioning.controller_cap":
      return "SkyServe controller at services cap";
    case "provisioning.auth_failed":
      return "cloud credentials rejected";
    case "provisioning.region_unavailable":
      return "region/zone unavailable";
    case "provisioning.image_unavailable":
      return "docker image unavailable";
    case "provisioning.ssh_unreachable":
      return "replica SSH refused (image missing sshd?)";
    case "provisioning.unreachable":
      return "cloud API unreachable";
    case "provisioning.no_clouds":
      return "no clouds enabled";
    case "provisioning.failed":
      return "provisioning failed";
    default:
      return kind;
  }
}

/**
 * "Where" column header — shows the (cloud, region) candidates
 * SkyPilot may pick from for this pool. We dedupe to *cloud only* in
 * the header (regions only get inline if they're pinned) so the
 * header reads as a compact filter chip set rather than the previous
 * full callout.
 *
 * The actual cloud/region a specific replica landed on is rendered
 * by `WhereCell` in each row — keeping the header for "what the
 * optimizer was allowed to pick" and the cell for "what it picked"
 * separates the two questions cleanly.
 *
 * 0 candidates is the only state that needs page chrome — handled
 * by `NoProvidersBanner` above the table.
 */
function WhereHeader({ pool }: { pool: Pool }) {
  const placements = usePoolPlacements(pool.id);
  const clouds = useMemo(() => {
    const seen: string[] = [];
    for (const p of placements) {
      if (!seen.includes(p.source_cloud)) seen.push(p.source_cloud);
    }
    return seen;
  }, [placements]);
  if (clouds.length === 0) {
    return <span>Where</span>;
  }
  return (
    <span
      style={{ display: "inline-flex", gap: 6, alignItems: "center" }}
      title="Provider candidates SkyPilot can pick from for this pool"
    >
      <span>Where</span>
      <span style={{ display: "inline-flex", gap: 4 }}>
        {clouds.map((c) => (
          <span key={c} className="cloud-tag">
            {humanCloudLabel(c)}
          </span>
        ))}
      </span>
    </span>
  );
}

/**
 * Per-row Where cell: cloud chip + region + instance type, all on
 * one line. The cloud chip is a flat tag (no dot) to keep the row
 * horizontally compact — the row's left border already encodes
 * blue/green role information for rollout context.
 */
function WhereCell({
  cloud,
  region,
  instance,
}: {
  cloud: string | null;
  region: string | null | undefined;
  instance: string | null | undefined;
}) {
  if (!cloud && !region && !instance) {
    return <span className="muted">—</span>;
  }
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      {cloud && <span className="cloud-tag">{humanCloudLabel(cloud)}</span>}
      {region && <span className="muted">{region}</span>}
      {instance && (
        <span className="mono muted" style={{ fontSize: 11.5 }}>
          {instance}
        </span>
      )}
    </span>
  );
}

/**
 * Above-table warning when the pool has zero provider candidates.
 * The previous `PlacementsCallout` rendered both the warning and the
 * candidate list inline — we split them up so the candidate list
 * lives in the table header (`WhereHeader`) and only the
 * actionable-error case still produces page chrome.
 */
function NoProvidersBanner({ pool }: { pool: Pool }) {
  const placements = usePoolPlacements(pool.id);
  if (placements.length > 0) return null;
  return (
    <div className="flash" style={{ margin: "0 14px 10px" }}>
      no providers enabled — enable at least one provider on the
      Providers tab (Global / Service / Pool override) before this
      pool can provision.
    </div>
  );
}

/**
 * `3m` / `2h` / `5d` style relative-duration formatter for table
 * cells. Mirrors the local helper in `PodMetrics.tsx` — kept
 * inline rather than extracted to a shared util because the two
 * call sites have different rounding tradeoffs (this one prefers
 * the *largest* unit so the column stays narrow).
 */
function formatRelativeShort(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

/**
 * Which cloud a replica was provisioned into.
 *
 * Authoritative source is `replica.cloud`, written by the reconciler
 * from SkyPilot's `launched_resources.cloud` view. The placement-based
 * inference below is kept as a fallback for the brief window before
 * SkyPilot has reported a launched_resources view (the column is NULL
 * during early provisioning) — same convention as `PlacementBreakdown`
 * on the Dashboard tab.
 *
 * Falls back to the lone unique cloud in the pool when the pool has
 * exactly one (the common case during the initial single-cloud
 * rollout), and to `null` when ambiguity remains (multi-cloud pool +
 * a replica whose region doesn't match any pinned placement).
 */
function inferReplicaCloud(
  replica: Replica,
  placements: ReadonlyArray<ResolvedPlacement>,
): string | null {
  if (replica.cloud) return replica.cloud;
  if (placements.length === 0) return null;
  if (replica.region) {
    const lower = replica.region.toLowerCase();
    const pinned = placements.find(
      (p): p is ResolvedPlacement & { region: string } =>
        p.region != null && lower.startsWith(p.region.toLowerCase()),
    );
    if (pinned) return pinned.source_cloud;
  }
  // Either the replica's region wasn't reported yet (pending /
  // provisioning) or it didn't match any pinned placement. Fall back
  // to the single placement cloud when unambiguous.
  const uniqueClouds = Array.from(
    new Set(placements.map((p) => p.source_cloud)),
  );
  if (uniqueClouds.length === 1) return uniqueClouds[0];
  return null;
}

/**
 * Per-replica live counters returned by `/pools/{id}/replica-runtime`.
 *
 * `active_websockets` powers the WS-aware drain orchestrator (replicas
 * with 0 active WS are picked first when scaling down). `in_flight_short_lived`
 * gates the `draining → shutting_down` transition. Both come from the
 * controller's Prometheus + Envoy stats helpers (see
 * `controller/controller/draining/ws_metrics.py`).
 */
interface ReplicaRuntime {
  active_websockets: number;
  in_flight_short_lived: number;
}
